import { forwardRef, MouseEvent, FocusEvent, KeyboardEvent } from "react";
import { Button, ButtonProps } from "../Button";
import { useMergeRefs } from "../../hooks";
import { usePopoverContext } from "./usePopoverContext";
import {
  PopoverLegacyButton,
  PopoverLegacyButtonProps,
} from "../PopoverLegacy";
import { supportsPopover } from "../../internal/functions/supportsPopover";

/**
 * Props for the PopoverButton component
 * @extends ButtonProps
 */
export type PopoverButtonProps = ButtonProps;

/**
 * PopoverButton component for creating button-style triggers.
 *
 * Features:
 * - Button styling and behavior with all Button props
 * - Automatic popover opening on click
 * - Hover interaction support for tooltip mode
 * - Focus management and keyboard navigation
 * - Accessibility attributes (aria-haspopup, aria-expanded, aria-details)
 * - Legacy popover support with fallback
 * - Context integration with parent popover
 * - Event handling for mouse, keyboard, and focus events
 *
 * @example
 * <Popover.Button>Click to open</Popover.Button>
 *
 * @example
 * <Popover.Button appearance="primary" size="large" icon={InfoIcon}>
 *   Information
 * </Popover.Button>
 *
 * @example
 * <Popover.Button onClick={() => console.log('Custom click handler')}>
 *   Custom handler
 * </Popover.Button>
 */
export const PopoverButton = forwardRef<HTMLButtonElement, PopoverButtonProps>(
  function PopoverButton(props, ref) {
    const {
      children,
      size,
      onMouseEnter,
      onMouseLeave,
      onClick,
      onKeyDown,
      onFocus,
      onBlur,
      ...rest
    } = props;
    const context = usePopoverContext();

    const onMouseEnterHandler = (e: MouseEvent<HTMLButtonElement>) => {
      onMouseEnter?.(e);
      context?.invoker.onMouseEnter();
    };
    const onMouseLeaveHandler = (e: MouseEvent<HTMLButtonElement>) => {
      onMouseLeave?.(e);
      context?.invoker.onMouseLeave();
    };
    const onFocusHandler = (e: FocusEvent<HTMLButtonElement>) => {
      onFocus?.(e);
      context?.invoker.onFocus();
    };
    const onBlurHandler = (e: FocusEvent<HTMLButtonElement>) => {
      onBlur?.(e);
      context?.invoker.onBlur();
    };
    const onKeyDownHandler = (e: KeyboardEvent<HTMLButtonElement>) => {
      onKeyDown?.(e);
      context?.invoker.onKeyDown(e);
    };

    const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      context?.invoker.onClick();
    };

    const buttonRef = useMergeRefs([context?.invoker.ref, ref]);

    if (!supportsPopover())
      return (
        <PopoverLegacyButton
          {...(props as PopoverLegacyButtonProps)}
          ref={ref}
        />
      );

    return (
      <Button
        ref={buttonRef}
        size={size}
        onClick={onClickHandler}
        onKeyDown={onKeyDownHandler}
        onMouseEnter={onMouseEnterHandler}
        onMouseLeave={onMouseLeaveHandler}
        onFocus={onFocusHandler}
        onBlur={onBlurHandler}
        data-anv="popover-button"
        aria-haspopup={context?.invoker["aria-haspopup"]}
        aria-expanded={context?.invoker["aria-expanded"]}
        aria-details={context?.invoker["aria-details"]}
        data-state={context?.invoker["data-state"]}
        {...rest}
      >
        {children}
      </Button>
    );
  },
);
